'use client';

import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { formatMakeName } from '@/utils/formatters';

import type { BrandGroup } from '@/lib/api';

// Brand hub links are rendered from server-fetched data (see
// app/shop-by-vehicle/page.tsx) so they are plain anchors in the initial
// HTML - crawlers and no-JS visitors can reach every brand page without the
// client-side vehicle finder.
const SECTIONS = [
  {
    collectionId: 1,
    heading: 'Popular Performance Car Air Filters',
    intro: 'Shop Green Filter applications for popular performance vehicles:',
    label: (title: string) => title,
  },
  {
    collectionId: 2,
    heading: 'Popular Pickup Truck Air Filters',
    intro: 'Find washable performance air filters for popular pickup trucks:',
    label: formatMakeName,
  },
  {
    collectionId: 5,
    heading: 'Shop Engine Air Filters by Brand',
    intro: 'Browse Green Filter applications by vehicle manufacturer:',
    label: formatMakeName,
  },
  {
    collectionId: 4,
    heading: 'Performance & Supercar Air Filters',
    intro: 'Find Green Filter applications for high-performance and exotic vehicles:',
    label: formatMakeName,
  },
];

interface ShopElement1Props {
  brandGroups: BrandGroup[];
}

const ShopElement1 = ({ brandGroups }: ShopElement1Props) => {
  return (
    <ShopElement1Wrapper>
      {SECTIONS.map(({ collectionId, heading, intro, label }) => {
        const brands = brandGroups.find((group) => group.id === collectionId)?.brands ?? [];
        if (brands.length === 0) return null;

        return (
          <div className="container-4 w-container" key={collectionId}>
            <h2 className="heading-11">{heading}</h2>
            <div className="shop-vehicle-intro">{intro}</div>
            <div className="shop-vehicle-row w-row">
              {brands.map((make) => (
                <div className="shop-vehicle-make w-col w-col-2" key={make.slug}>
                  <Link className="link-block" href={`/brand/${make.slug}`}>
                    <div className="column-div custom-button-temp">
                      <div className="text-block car-carousel">{label(make.title)}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </ShopElement1Wrapper>
  );
};

export default ShopElement1;

const ShopElement1Wrapper = styled.div`
  position: relative;

  background-color: #fff;

  .container-4 {
    padding-bottom: 50px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    max-width: 940px;
  }

  .shop-vehicle-intro {
    ${TYPOGRAPHY.bodySM}
    color: ${COLORS.gray700};
    margin: 0 0 24px;
  }

  .w-container:before,
  .w-container:after {
    content: ' ';
    display: table;
  }

  .w-row:before,
  .w-row:after {
    content: ' ';
    display: table;
  }

  .heading-11 {
    margin-bottom: 12px;
    font-family: Lato, sans-serif;
    font-size: 28px;
    font-weight: 800;
    text-align: center;
    color: #1a1a1a;
  }

  .w-col-2 {
    width: 16.66666667%;
  }

  .w-col {
    position: relative;
    min-height: 1px;
    padding-left: 10px;
    padding-right: 10px;
  }

  .shop-vehicle-row.w-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .link-block {
    width: auto;
    margin-bottom: 0px;
    padding: 0px;
    text-align: center;
    text-decoration: none;
    max-width: 100%;
    display: inline-block;
  }

  .column-div {
    padding-right: 0px;
    padding-left: 0px;
    text-align: center;
  }

  .custom-button-temp {
    padding: 12px 16px;
    background-color: #f5f5f5;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      background-color: ${COLORS.brandGreen};
      border-color: ${COLORS.brandGreen};

      .text-block {
        color: #fff;
      }
    }
  }

  .text-block.car-carousel {
    font-family: Lato, sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1a1a1a;
    transition: color 0.3s ease;
  }

  .make-image {
    height: 100px;
    display: table-cell;
    vertical-align: bottom;
  }

  .product-image {
    width: 200px;
    max-width: 100%;
    vertical-align: middle;
    display: inline-block;
    border: 0;
  }

  @media screen and (max-width: 767px) {
    .shop-vehicle-row.w-row {
      gap: 8px;
      padding: 0 10px;
      display: flex !important;
      flex-wrap: wrap !important;
      justify-content: center !important;
    }

    .shop-vehicle-row .w-col.w-col-2,
    .shop-vehicle-make.w-col.w-col-2 {
      width: 33.333% !important;
      flex: 0 0 calc(33.333% - 8px) !important;
      max-width: calc(33.333% - 8px) !important;
      margin-bottom: 12px;
      padding-left: 5px;
      padding-right: 5px;
      float: none !important;
    }

    .heading-11 {
      margin-bottom: 10px;
      font-size: 20px;
      line-height: 1.3;
      padding: 0 15px;
    }

    .container-4 {
      padding-top: 30px;
      padding-bottom: 30px;
    }

    .shop-vehicle-intro {
      font-size: ${FONT_SIZE.body.xs};
      padding: 0 15px;
      margin-bottom: 18px;
    }
  }

  @media screen and (max-width: 479px) {
    .shop-vehicle-row .w-col.w-col-2,
    .shop-vehicle-make.w-col.w-col-2 {
      width: 33.333% !important;
      flex: 0 0 calc(33.333% - 8px) !important;
      max-width: calc(33.333% - 8px) !important;
    }

    .heading-11 {
      font-size: 18px;
      margin-bottom: 20px;
    }
  }
`;
